import { css } from 'styled-components';

/**
 * AXON-3585: how a recurring-template slot looks, in one place.
 *
 * These rules were written for the DT week view on the provider profile and
 * scoped to it. The clinic-wide day grid draws the same template through the
 * same slot renderer, so it needs the same rules rather than an approximation
 * of them — a scheduler moving between the two views should not have to learn
 * the template twice.
 *
 * Parameterised by the selector that scopes them, because that is the only
 * thing that legitimately differs between the two surfaces. Add a rule here,
 * not in one view's stylesheet, or the views drift apart again.
 */
export const templateSlotStyles = (scope: string) => css`
  /* A 30-minute row is 48px, which is what gives a short bar room for two text
     rows. react-big-calendar's own default is 40px, and at that height the
     start time is clipped. */
  ${scope} .rbc-timeslot-group {
    min-height: 48px;
    border-bottom: 1px solid #e0e0e0 !important;
  }
  /* The shared calendar wrapper strips the gutter's horizontal lines; restored
     so the time column keeps aligning with the rows beside it. */
  ${scope} .rbc-time-gutter .rbc-timeslot-group {
    border-bottom: 1px solid #e0e0e0 !important;
  }
  ${scope} .rbc-event.availability {
    background: var(--commen-white_state-main, #fcfcfc) !important;
    border: 2px dashed rgba(18, 18, 18, 0.12) !important;
    border-radius: 4px !important;
    opacity: 1 !important;
    box-shadow: none !important;
    padding: 8px !important;
    width: calc(100% - 5px) !important;
    left: 0 !important;
    overflow: hidden;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    text-align: center !important;
  }
  /* Keep the fill inside the border box so the transparent border reads as a
     gap. Without this, two adjacent same-coloured slots (e.g. Notes ending as
     Meeting begins) merge into one indistinguishable block. */
  ${scope} .rbc-event.availability.avail-notes,
  ${scope} .rbc-event.availability.avail-meeting {
    border-style: solid !important;
    border-color: transparent !important;
    background: #e8eef7 !important;
    background-clip: padding-box !important;
  }
  ${scope} .rbc-event.availability.avail-lunch {
    border-style: solid !important;
    border-color: transparent !important;
    background: #fbf3df !important;
    background-clip: padding-box !important;
  }
  ${scope} .rbc-event.availability .rbc-event-content {
    background: transparent !important;
    background-color: transparent !important;
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
  }
  /* Template slots stack their label over the start time, matching the way a
     booked slot reads. Short slots show the label only. */
  ${scope} .rbc-event.availability .provider-avail {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    min-width: 0;
    width: 100%;
    overflow: hidden;
  }
  ${scope} .rbc-event.availability .avail-time {
    font-family: 'Roboto', 'Lato', sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: rgba(18, 18, 18, 0.5);
    white-space: nowrap;
  }
  ${scope} .rbc-event.availability .avail-label-text {
    display: block;
    text-align: center;
    /* Figma typography/body2: Roboto 14 / 400, line-height 1.43, ls 0.17 */
    font-family: 'Roboto', 'Lato', sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 1.43;
    letter-spacing: 0.17px;
    color: rgba(18, 18, 18, 0.6);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  ${scope} .rbc-event.availability.avail-lunch .avail-label-text,
  ${scope} .rbc-event.availability.avail-notes .avail-label-text,
  ${scope} .rbc-event.availability.avail-meeting .avail-label-text {
    color: #41576a;
    font-weight: 600;
  }
  ${scope} .rbc-event.event-appointment.ProviderEvent,
  ${scope} .rbc-event.event-appointment.ProviderEvent:hover {
    width: calc(100% - 5px) !important;
    max-width: calc(100% - 5px) !important;
    left: 0 !important;
    right: 0 !important;
    margin: 0 !important;
    min-height: 32px !important;
    border: 2px solid #ffffff !important;
    border-radius: 6px !important;
    padding: 6px 8px !important;
    box-shadow: none !important;
    overflow: hidden;
  }
  ${scope} .rbc-event.event-appointment.ProviderEvent.invisible-appt {
    background: #ebebeb !important;
    border: 2px dotted #52606b !important;
  }
  ${scope} .rbc-event.ProviderEvent.invisible-appt .appt-chip {
    background: #8c8c8c !important;
    color: #ffffff !important;
  }
  ${scope} .rbc-event.ProviderEvent.invisible-appt .appt-name,
  ${scope} .rbc-event.ProviderEvent.invisible-appt .appt-type-admin {
    color: #8c8c8c !important;
  }
  ${scope} .rbc-event.event-appointment.ProviderEvent .rbc-event-content {
    background: transparent !important;
    background-color: transparent !important;
    padding: 0 !important;
    height: 100%;
  }
  ${scope}
    .rbc-event.event-appointment.ProviderEvent
    .rbc-event-content
    > div:before {
    display: none !important;
  }
  ${scope} .rbc-event.ProviderEvent .rbc-event-label,
  ${scope} .rbc-event.availability .rbc-event-label {
    display: none !important;
  }
  ${scope} .provider-appt {
    display: flex;
    flex-direction: column;
    gap: 4px;
    height: 100%;
    overflow: hidden;
  }
  ${scope} .provider-appt.admin {
    align-items: center;
    justify-content: center;
    text-align: center;
  }
  /* Row 2 for a confirmed-but-not-started series: when does it begin. The time
     drops out first if the row runs out of width; the date always survives. */
  ${scope} .provider-appt .appt-starts {
    display: flex;
    align-items: baseline;
    gap: 3px;
    min-width: 0;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: #21303a;
    white-space: nowrap;
    overflow: hidden;
  }
  ${scope} .provider-appt .appt-starts .starts-dot {
    flex: 0 0 auto;
    color: #2f7ed8;
    font-size: 9px;
    line-height: 16px;
  }
  ${scope} .provider-appt .appt-starts .starts-time {
    flex: 0 1 auto;
    min-width: 0;
    font-weight: 400;
    color: rgba(33, 48, 58, 0.65);
    overflow: hidden;
    text-overflow: clip;
  }
  /* Row 3: appointment type, with the full bar width to itself so it reads. */
  ${scope} .provider-appt .appt-chip {
    align-self: flex-start;
    max-width: 100%;
    padding: 0 10px;
    border-radius: 12px;
    color: #ffffff;
    font-size: 12px;
    font-weight: 700;
    line-height: 18px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  ${scope} .provider-appt .appt-type-admin {
    font-size: 13px;
    font-weight: 700;
    line-height: 16px;
    color: #21303a;
    text-align: center;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* Row 1 of a booked slot: the client name gets the full width. */
  ${scope} .provider-appt .appt-headline {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
    overflow: hidden;
  }
  ${scope} .provider-appt .appt-name {
    flex: 0 1 auto;
    min-width: 0;
    font-size: 13px;
    font-weight: 700;
    line-height: 16px;
    color: #21303a;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  /* The client's name opens their profile. Underlined rather than recoloured,
     so the bar's own colour keeps meaning what it means. */
  ${scope} .provider-appt .appt-name a {
    color: inherit;
    text-decoration: underline;
  }
  /* Row 2: start time on its own line. */
  ${scope} .provider-appt .appt-time {
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: rgba(33, 48, 58, 0.65);
    white-space: nowrap;
  }
  /* "Ending" indicator (recurring series ending within 4 weeks) — same as planner */
  ${scope} .provider-appt .appt-ending {
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 0 0 auto;
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    color: #21303a;
    white-space: nowrap;
  }
  /* Proposed slot: the grey bar already sets it apart, so the row states why
     rather than competing for attention. Reads in the same shape as Starts and
     Ending so the three states are learned once. */
  ${scope} .provider-appt .appt-pending {
    display: flex;
    align-items: baseline;
    gap: 3px;
    min-width: 0;
    font-size: 12px;
    font-weight: 600;
    line-height: 16px;
    color: #52606b;
    white-space: nowrap;
  }
  ${scope} .provider-appt .appt-pending .pending-dot {
    flex: 0 0 auto;
    color: #52606b;
    font-size: 11px;
    line-height: 16px;
  }
  ${scope} .provider-appt .appt-pending .pending-time {
    flex: 0 1 auto;
    min-width: 0;
    font-weight: 400;
    color: rgba(82, 96, 107, 0.75);
    overflow: hidden;
    text-overflow: clip;
  }
  /* The slot time sits beside the end date at the lighter weight it carries on
     its own row, so the date stays the thing the eye lands on. Mirrors
     .appt-starts .starts-time. */
  ${scope} .provider-appt .appt-ending .ending-time {
    flex: 0 1 auto;
    min-width: 0;
    font-weight: 400;
    color: rgba(33, 48, 58, 0.65);
    overflow: hidden;
    text-overflow: clip;
  }
  ${scope} .provider-appt .ending-dot {
    color: #cc7a00;
    font-size: 14px;
    line-height: 1;
  }
  /* Short (<=30 min) appointments: two rows fit, so the type chip is dropped
     and the name + time pair is centred vertically. */
  ${scope} .rbc-event.event-appointment.ProviderEvent.appt-compact {
    padding: 2px 8px !important;
  }
  ${scope} .rbc-event.appt-compact .provider-appt {
    justify-content: center;
    gap: 2px;
  }
`;
